import React, { Dispatch, SetStateAction } from 'react';
import { StyleSheet } from 'react-native';
import { useSelector } from 'react-redux';
import SpaceUtilizationWidget from './SpaceUtilizationWidget';
import BarGraph from '../../../../graphs/BarGraph';
import { DateRange } from '../../../../redux/dateRangeSlice';
import LineGraph from '../../../../graphs/LineGraph';
import Colors from '../../../../constants/Colors';
import TimeIcon16 from '../../../16pxIcons/TimeIcon16';
import { ChartDataPoint } from '../../../../graphs/axisUtils';
import { getRangeHours } from '../../../../constants/Utils';
import { getDayIndex } from '../Utils';

interface TimeComparisonGraphProps {
  hoursBookedData: ChartDataPoint[] | null,
  timeSpentData: ChartDataPoint[] | null,
  hoursBookedDataCompared: ChartDataPoint[] | null,
  timeSpentDataCompared: ChartDataPoint[] | null,
  selectedDayIndex: number,
  setSelectedDayIndex: Dispatch<SetStateAction<number>>,
}

const alignTwoSeriesByDay = (a: ChartDataPoint[], b: ChartDataPoint[]) => {
  const aMap = new Map<string, ChartDataPoint>();
  const bMap = new Map<string, ChartDataPoint>();

  a.forEach((p: ChartDataPoint) => aMap.set(p.date, p));
  b.forEach((p: ChartDataPoint) => bMap.set(p.date, p));

  const allKeys = Array.from(new Set([...aMap.keys(), ...bMap.keys()]))
    .sort((k1, k2) => new Date(k1).getTime() - new Date(k2).getTime());

  const alignedA: ChartDataPoint[] = allKeys.map((k, index) => {
    const existing = aMap.get(k);
    return existing
      ? { ...existing, index }
      : { index, date: k, value: 0 };
  });

  const alignedB: ChartDataPoint[] = allKeys.map((k, index) => {
    const existing = bMap.get(k);
    return existing
      ? { ...existing, index }
      : { index, date: k, value: 0 };
  });

  return { alignedA, alignedB };
};

const TimeComparisonGraph = ({
  hoursBookedData: chartData, timeSpentData: alternateData,
  hoursBookedDataCompared: comparisonData, timeSpentDataCompared: alternateComparisonData,
  selectedDayIndex, setSelectedDayIndex,
}: TimeComparisonGraphProps) => {
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => state.dateRange);
  const compareRange: DateRange = useSelector((state:
    { dateComparisonRange: DateRange }) => (state.dateComparisonRange));
  const showBarGraph: boolean = !(!!compareRange.start && !!compareRange.end)
    && getRangeHours(range) / 24 < 8;
  const unit: string = getRangeHours(range) > 24 ? 'Hours' : 'Minutes';
  const showDayDropdown: boolean = !!setSelectedDayIndex && selectedDayIndex !== undefined
    && ![0, 7].includes(range.selectedRange);
  const title: string = 'Booked time';
  const alternateTitle: string = 'Time occupied';

  let filteredChartData = chartData;
  let filteredAlternateData = alternateData;
  let filteredComparisonData = comparisonData;
  let filteredAlternateComparisonData = alternateComparisonData;

  if (showDayDropdown && selectedDayIndex !== undefined) {
    filteredChartData = chartData
      ?.filter((d) => getDayIndex(new Date(d.date)) === selectedDayIndex);
    filteredAlternateData = alternateData
      ?.filter((d) => getDayIndex(new Date(d.date)) === selectedDayIndex);
    filteredComparisonData = comparisonData
      ?.filter((d) => getDayIndex(new Date(d.date)) === selectedDayIndex);
    filteredAlternateComparisonData = alternateComparisonData
      ?.filter((d) => getDayIndex(new Date(d.date)) === selectedDayIndex);
  }

  const {
    alignedA: paddedChartData,
    alignedB: paddedAlternateData,
  } = alignTwoSeriesByDay(filteredChartData ?? [], filteredAlternateData ?? []);

  const {
    alignedA: paddedComparisonData,
    alignedB: paddedAlternateComparisonData,
  } = alignTwoSeriesByDay(filteredComparisonData ?? [], filteredAlternateComparisonData ?? []);

  return <SpaceUtilizationWidget
    icon={<TimeIcon16 />}
    title='Booked time vs time spent'
    content={showBarGraph ? <BarGraph
      {...{
        unit, title, alternateTitle, range,
      }}
      chartData={paddedChartData}
      alternateData={paddedAlternateData}
      selectedColor={Colors.light.hereworksDarkBlue300}
      alternateColor={Colors.light.hereworksDarkBlue100}
      chartHeight={315}
      horizontalTickCount={12}
      showLabels={false}
      stackAlternate
      showLegend
      hideTimeOnTooltip
      paddingStartOffset={paddedChartData.length === 1 ? 120 : undefined}
    /> : <LineGraph
      {...{
        unit,
        title,
        alternateTitle,
        range,
      }}
      chartData={paddedChartData}
      alternateData={paddedAlternateData}
      comparisonData={comparisonData ? paddedComparisonData : null}
      alternateComparisonData={alternateComparisonData ? paddedAlternateComparisonData : null}
      alternateColor={Colors.light.hereworksBlue100}
      noTitle
      chartHeight={420}
      style={styles.lineGraph}
      showLegend
      hideTimeOnTooltip
      domainYLimit={24}
    />}
    {...{ selectedDayIndex, setSelectedDayIndex, showDayDropdown }}
    plural={![0, 1, 7].includes(range.selectedRange)}
  />;
};

const styles = StyleSheet.create({
  lineGraph: {
    marginTop: -70,
    marginStart: -24,
    marginEnd: 20,
  },
});

export default TimeComparisonGraph;
